import { ChangeDetectorRef, Component, OnDestroy, OnInit, Input } from '@angular/core';
import { BaseProductDetailComponent } from '@components/products/base-product-detail.component';
import {
  FundListingBRConfiguration,
  FundListingComponentType,
  FundListingDataFormat,
  FundListingState,
} from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundListingDisplayComponent } from '../fund-listing-display-component.interface';
import { FundListingService } from '../services/fund-listing.service';
import { TranslateService } from '@components/shared/translate/translate.service';
import { Location } from "@angular/common";
import { InvestNowService } from "@services/invest-now.service";
import { AnalyticsService } from "@services/analytics.service";

const logger = Logger.getLogger('FundCardsComponent');

@Component({
  selector: 'ft-fund-cards',
  templateUrl: './fund-cards.component.html',
  styleUrls: ['./fund-cards.component.scss'],
})
export class FundCardsComponent
  extends BaseProductDetailComponent
  implements FundListingDisplayComponent, OnInit, OnDestroy {
  brConfiguration: FundListingBRConfiguration;
  dataSubscription: Subscription;
  fundCardsState: FundListingState;
  fundCardsData: FundListingDataFormat[] | FundListingDataFormat[];
  fundDataAll: FundListingDataFormat[] | FundListingDataFormat[];
  isLoaded: boolean;
  componentType: FundListingComponentType;
  badgeValue: string;
  focusTabName: string;
  focusFunds: any = [];
  sideFilterOptions: any;
  isMobileView: boolean;
  calculatorPage: boolean = false;
  @Input() isCallback = false;

  constructor(
    private fundListingService: FundListingService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private location: Location,
    private analyticsService: AnalyticsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.badgeValue = this.translateService.instant("products.focused-funds-badge");
    this.focusTabName = this.translateService.instant("products.home-fund-listing-focus-tab-name");
    let focusFunds = this.translateService.instant("products.focused-funds-config");
    const focusFundsArray = focusFunds?.split(",");
    this.focusFunds = focusFundsArray?.map(number => {
      return {"fundId": number};
    });
    this.isMobileView = window.innerWidth < 768;
    if (this.location.path().indexOf('-calculator') > 0 || this.location.path().indexOf('sipislikeagoodemi') > 0) {
      this.calculatorPage = true;
    }
  }

  /**
   * Initialized when current commponent gets loaded.
   * @param brConfiguration bloomreach configuration received
   */
  populate(brConfiguration: FundListingBRConfiguration): void {
    // Save BR configuration only once.
    if (!this.brConfiguration) {
      this.brConfiguration = brConfiguration;
      this.brConfiguration.config.componentType = FundListingComponentType.PPSS;
      this.componentType = FundListingComponentType.PPSS;
    }

    this.dataSubscription = this.fundListingService
      .getFundInformationState$()
      .subscribe(
        async (state) => {
          logger.debug('Mapped fund listing state: ', state);
          this.isLoaded = state.isLoaded;

          if (this.isLoaded) {
            // Assign component type to be rendered.

            // Assign received state for specific component type.
            this.fundCardsState = state;
            // Limit listing data for displaying fund cards.
            this.fundCardsData = []; // Initialize to blank when state changes.
            this.sideFilterOptions = state.filterDropdowns[0].filterItems.map((fund, index) => ({
              id: (index + 1).toString(),
              name: fund.label
            }));
            const ppssData: FundListingDataFormat[] = this.fundCardsState
              ?.ppssState.ppssData;
            this.fundDataAll = ppssData;
            this.fundCardsData = ppssData;

            let modifiedData = this.fundCardsData;
            // Sort the array based on averageAnnualReturnSinceInception
            modifiedData.sort((a, b) => {
              const returnA = parseFloat(a?.shareClassData?.performanceData?.averageAnnualReturnSinceInception);
              const returnB = parseFloat(b?.shareClassData?.performanceData?.averageAnnualReturnSinceInception);

              // Check if the parsed values are valid numbers
              const isValidNumberA = !isNaN(returnA);
              const isValidNumberB = !isNaN(returnB);

              // Handle cases where either or both values are not valid numbers
              if (isValidNumberA && isValidNumberB) {
                // Sort based on valid numeric values
                return returnB - returnA;
              } else if (isValidNumberA && !isValidNumberB) {
                // Sort valid numbers before non-valid numbers
                return -1;
              } else if (!isValidNumberA && isValidNumberB) {
                // Sort valid numbers before non-valid numbers
                return 1;
              } else {
                // Maintain the current order if neither value is a valid number
                return 0;
              }
            });
            for (let i = 0; i < modifiedData.length; i++) {
              let selectedFund = modifiedData[i];

              // Find the object with value "averageAnnualReturnSinceInception" in ppssPeriodDropdownItems
              // Add selectedDropdown to the main object
              selectedFund.selectedDropdown = selectedFund.ppssPeriodDropdownItems.find(item => item.value === "averageAnnualReturnSinceInception");
            }
            // Adding the Focus Badge.
            this.fundCardsData = modifiedData.map(fund => {
              // Check if the fundId is in focusFunds array
              if (this.focusFunds.some(focusFund => focusFund.fundId === fund.fundId)) {
                // Add the badge property
                return {...fund, badge: this.badgeValue ? this.badgeValue : 'focus'};
              }
              return fund;
            });

            // Sort the array based on badge and prioritize funds with badge: focus
            this.fundCardsData.sort((a, b) => {
              // Check if funds have the badge: focus property
              const aHasFocusBadge = a.badge === "focus";
              const bHasFocusBadge = b.badge === "focus";

              // Prioritize funds with the badge: focus property
              if (aHasFocusBadge && !bHasFocusBadge) {
                return -1;
              } else if (!aHasFocusBadge && bHasFocusBadge) {
                return 1;
              }

              // If both funds have the badge: focus property or neither does,
              // maintain the current order in the array
              return 0;
            });
          }
          this.cdr.detectChanges();
        },
        (error) => {
          logger.error('Error in invoking fund listing service: ', error);
          this.isLoaded = false;
          this.cdr.detectChanges();
        }
      );

    this.fundListingService.populate(this.brConfiguration);
  }

  ngOnDestroy(): void {
    // Clean up subscription to avoid memory leaks.
    this.dataSubscription?.unsubscribe();
  }

  triggerGA(label: string) {
    let gaCat = 'Knowledge Centre';
    if (this.location.path().indexOf('tools/funds-watchlist') > 0) {
      gaCat = 'Watchlist';
    } else if (this.location.path().indexOf('tools/fund-comparator') > 0) {
      gaCat = 'Compare';
    }

    this.analyticsService.trackEvent({
      event: 'Event Fund Explorer',
      ft_event: 'ft_CalculateNow_Clicked',
      category: gaCat,
      action: this.translateService.instant(label),
      label: 'PlanYourGoal',
    });
  }
}
